import { collection, doc, getDocs, limit, query } from "firebase/firestore";
import { runtimeDb as db } from "../../firestoreStudio/runtime/runtimeDb";
import { DELETE_BATCH_SIZE } from "../constants";
import { createBackupsBatched } from "./backupService";
import { commitBatches } from "./firestoreDataService";
import { ensureFreshAuthToken } from "./operationAuthService";
import { estimateBatchMeta } from "./costEstimatorService";

function matchesFind(value, find, { caseSensitive, regex, wholeWord }) {
  if (value == null) return find === "null" || find === "";
  const hay = String(value);
  const needle = String(find);
  if (regex) {
    try {
      const flags = caseSensitive ? "" : "i";
      return new RegExp(needle, flags).test(hay);
    } catch {
      return false;
    }
  }
  const h = caseSensitive ? hay : hay.toLowerCase();
  const n = caseSensitive ? needle : needle.toLowerCase();
  if (wholeWord) {
    const re = new RegExp(`\\b${n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, caseSensitive ? "" : "i");
    return re.test(hay);
  }
  return h.includes(n);
}

function replaceValue(value, find, replace, opts) {
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value) === String(find) ? coerceScalar(replace) : value;
  }
  if (value == null) return value;
  const hay = String(value);
  const needle = String(find);
  if (opts.regex) {
    try {
      const flags = opts.caseSensitive ? "g" : "gi";
      return hay.replace(new RegExp(needle, flags), replace);
    } catch {
      return hay;
    }
  }
  if (opts.wholeWord) {
    const re = new RegExp(`\\b${needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, opts.caseSensitive ? "g" : "gi");
    return hay.replace(re, replace);
  }
  if (!opts.caseSensitive) {
    const re = new RegExp(needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    return hay.replace(re, replace);
  }
  return hay.split(needle).join(replace);
}

function coerceScalar(raw) {
  const s = String(raw ?? "").trim();
  if (s === "true") return true;
  if (s === "false") return false;
  if (s === "null") return null;
  if (s !== "" && !Number.isNaN(Number(s)) && /^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  return raw;
}

export async function dryRunFindReplace(collectionName, field, find, replace, options) {
  const col = collectionName.trim();
  const snap = await getDocs(query(collection(db, col), limit(500)));
  const previewRows = [];
  let matchCount = 0;

  snap.docs.forEach((d) => {
    const data = d.data() || {};
    const val = data[field];
    if (!matchesFind(val, find, options)) return;
    matchCount += 1;
    const after = { ...data, [field]: replaceValue(val, find, replace, options) };
    previewRows.push({
      id: d.id,
      before: data,
      after,
      oldValue: val,
      newValue: after[field],
    });
  });

  const batchMeta = estimateBatchMeta(matchCount, DELETE_BATCH_SIZE);
  return {
    affectedDocuments: matchCount,
    previewRows: previewRows.slice(0, 20),
    previewIds: previewRows.slice(0, 20).map((r) => r.id),
    ...batchMeta,
    warnings: matchCount > 500 ? ["Sampled first 500 docs for preview; full run scans paginated."] : [],
  };
}

export async function executeFindReplace({
  collectionName,
  field,
  find,
  replace,
  options,
  adminUid,
  adminName,
  onProgress,
}) {
  const col = collectionName.trim();
  const started = Date.now();
  await ensureFreshAuthToken();

  const toUpdate = [];
  let cursor = null;
  while (true) {
    let q = query(collection(db, col), limit(500));
    if (cursor) {
      const { startAfter } = await import("firebase/firestore");
      q = query(collection(db, col), startAfter(cursor), limit(500));
    }
    const snap = await getDocs(q);
    if (snap.empty) break;
    snap.docs.forEach((d) => {
      const data = d.data() || {};
      if (matchesFind(data[field], find, options)) {
        toUpdate.push({
          id: d.id,
          data: { ...data, [field]: replaceValue(data[field], find, replace, options) },
          before: data,
        });
      }
    });
    if (snap.docs.length < 500) break;
    cursor = snap.docs[snap.docs.length - 1];
  }

  if (!toUpdate.length) return { updated: 0, failed: 0, seconds: 0 };

  const documents = {};
  toUpdate.forEach((u) => {
    documents[u.id] = u.before;
  });
  await createBackupsBatched({
    collectionName: col,
    documents,
    adminUid,
    adminName,
    operation: "find_replace",
    chunkSize: 100,
  });

  await ensureFreshAuthToken();
  const totalBatches = Math.ceil(toUpdate.length / DELETE_BATCH_SIZE);
  let updated = 0;
  let failed = 0;

  for (let i = 0; i < toUpdate.length; i += DELETE_BATCH_SIZE) {
    const batchNum = Math.floor(i / DELETE_BATCH_SIZE) + 1;
    const chunk = toUpdate.slice(i, i + DELETE_BATCH_SIZE);
    onProgress?.({ phase: "updating", batch: batchNum, totalBatches, message: `Batch ${batchNum} of ${totalBatches}` });
    try {
      await commitBatches(
        chunk.map((u) => ({ type: "update", ref: doc(db, col, u.id), data: u.data }))
      );
      updated += chunk.length;
    } catch {
      failed += chunk.length;
    }
  }

  return { updated, failed, seconds: Math.round((Date.now() - started) / 1000) };
}
